import { mobileViewportUnit } from '../../utils/mobileView';
import { MUI_THEME_Z_INDEX, Z_INDEX } from '../constants/theme';

const SAFE_TOP = 'env(safe-area-inset-top, 0px)';
const SAFE_BOTTOM = 'env(safe-area-inset-bottom, 0px)';

function drawerPaperHeight() {
  return `calc(100${mobileViewportUnit()} - ${SAFE_TOP} - ${SAFE_BOTTOM})`;
}

/**
 * MUI `sx` for Drawer `PaperProps` on mobile shell / native.
 * @param {'left'|'right'|'top'|'bottom'} anchor
 */
export function getMobileDrawerPaperSx(anchor = 'right') {
  if (anchor === 'bottom') {
    return {
      top: 'auto',
      bottom: SAFE_BOTTOM,
      left: 0,
      right: 0,
      width: '100%',
      height: 'auto',
      maxHeight: `calc(92${mobileViewportUnit()} - ${SAFE_TOP} - ${SAFE_BOTTOM})`,
      boxSizing: 'border-box',
    };
  }

  if (anchor === 'top') {
    return {
      top: SAFE_TOP,
      height: 'auto',
      maxHeight: `calc(85${mobileViewportUnit()} - ${SAFE_TOP} - ${SAFE_BOTTOM})`,
      boxSizing: 'border-box',
    };
  }

  const height = drawerPaperHeight();
  return {
    top: SAFE_TOP,
    height,
    maxHeight: height,
    boxSizing: 'border-box',
  };
}

/** Theme + global CSS overrides so portaled drawers/dialogs stay inside the mobile frame. */
export function buildMobileDrawerThemeOverrides() {
  const sideDrawerPaperFrame = {
    top: SAFE_TOP,
    height: drawerPaperHeight(),
    maxHeight: drawerPaperHeight(),
    boxSizing: 'border-box',
  };
  const fullPaperHeight = drawerPaperHeight();
  const compactDrawerContent = {
    '& .MuiTypography-h6': { fontSize: '1rem', lineHeight: 1.2 },
    '& .MuiTypography-subtitle1': { fontSize: '0.88rem', lineHeight: 1.25 },
    '& .MuiTypography-subtitle2': { fontSize: '0.78rem', lineHeight: 1.2 },
    '& .MuiTypography-body1': { fontSize: '0.86rem', lineHeight: 1.3 },
    '& .MuiTypography-body2': { fontSize: '0.76rem', lineHeight: 1.25 },
    '& .MuiTypography-caption': { fontSize: '0.64rem', lineHeight: 1.2 },
    '& .MuiButton-root': {
      minHeight: 32,
      fontSize: '0.72rem',
      borderRadius: 10,
      paddingTop: 3,
      paddingBottom: 3,
    },
    '& .MuiChip-root': {
      height: 20,
      fontSize: '0.62rem',
    },
    '& .MuiChip-label': {
      paddingLeft: 7,
      paddingRight: 7,
    },
    '& .MuiIconButton-root': {
      width: 32,
      height: 32,
      padding: 4,
    },
    '& .MuiInputBase-root': {
      minHeight: 38,
      fontSize: '0.82rem',
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.8rem',
    },
  };

  return {
    // Keep Menu/Select/Modal portals above mobile-shell drawers (1700).
    zIndex: MUI_THEME_Z_INDEX,
    components: {
      MuiDrawer: {
        styleOverrides: {
          root: {
            zIndex: Z_INDEX.DRAWER,
          },
          paper: compactDrawerContent,
          paperAnchorLeft: sideDrawerPaperFrame,
          paperAnchorRight: sideDrawerPaperFrame,
          paperAnchorTop: {
            top: SAFE_TOP,
            height: 'auto',
            maxHeight: `calc(85${mobileViewportUnit()} - ${SAFE_TOP} - ${SAFE_BOTTOM})`,
            boxSizing: 'border-box',
          },
          paperAnchorBottom: {
            top: 'auto',
            bottom: SAFE_BOTTOM,
            left: 0,
            right: 0,
            width: '100%',
            height: 'auto',
            maxHeight: `calc(92${mobileViewportUnit()} - ${SAFE_TOP} - ${SAFE_BOTTOM})`,
            boxSizing: 'border-box',
          },
        },
      },
      // Portaled overlays must sit above MuiDrawer (1700) on mobile.
      MuiMenu: {
        defaultProps: {
          disablePortal: false,
        },
        styleOverrides: {
          root: {
            zIndex: `${Z_INDEX.DRAWER_MENU} !important`,
          },
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiPopover: {
        defaultProps: {
          disablePortal: false,
        },
        styleOverrides: {
          root: {
            zIndex: `${Z_INDEX.DRAWER_POPOVER} !important`,
          },
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          popper: {
            zIndex: `${Z_INDEX.DRAWER_MENU} !important`,
          },
          paper: {
            backgroundImage: 'none',
          },
        },
      },
      MuiPickersPopper: {
        styleOverrides: {
          root: {
            zIndex: `${Z_INDEX.DRAWER_POPOVER} !important`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: {
            zIndex: Z_INDEX.MODAL,
          },
          paper: {
            margin: 0,
            maxHeight: fullPaperHeight,
          },
          paperFullScreen: {
            top: SAFE_TOP,
            height: fullPaperHeight,
            maxHeight: fullPaperHeight,
            boxSizing: 'border-box',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            paddingBottom: `calc(16px + ${SAFE_BOTTOM})`,
          },
        },
      },
    },
  };
}

export default buildMobileDrawerThemeOverrides;
